import t from '@jetshop/intl';
import React, { useEffect } from 'react';
import { styled } from 'linaria/react';
import { theme } from '../../../Theme';
import { LoadableProductListCard } from '../../../CategoryPage/ProductGridWindow';
import { DynamicCategoryRenderer } from '../../../ContentEditor/DynamicCategoryRenderer/DynamicCategoryRenderer';
import { DownloadableFiles } from './DownloadableFiles';

const ButtonRow = styled('div')`
  position: relative;
  width: 100%;
  max-width: 1200px;
  padding: 0 20px;
  margin: 20px auto;
  .inner {
    position: relative;
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    grid-gap: 30px;
    &:after {
      content: '';
      height: 2px;
      background: #f7f7f7;
      position: absolute;
      left: 0;
      right: 0;
      top: 100%;
    }
  }
`;

const Toggle = styled('div')`
  position: relative;
  height: 30px;
  line-height: 30px;
  cursor: pointer;
  text-transform: uppercase;
  color: #282828;
  font-size: 14px;
  font-style: normal;
  letter-spacing: 0.56px;
  text-transform: uppercase;

  &:after {
    content: '';
    background: ${theme.colors.black};
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    transition: all, 0.1s;
    width: 0;
    height: 4px;
  }

  &:hover {
    &:after {
      width: 100%;
    }
  }

  &.active {
    font-weight: 700;
  }
`;

const TabContent = styled('div')`
  width: 100%;
  max-width: 1200px;
  padding: 0 20px 20px 20px;
  margin: auto;

  .brandheader-textitem {
    padding: 20px 0;
  }
`;

export const TabInner = styled('div')`
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 1rem;
  p {
    font-size: 13px;
    line-height: 1.6;
    margin-bottom: 1rem;
  }
  h2,
  h3,
  h4,
  h5 {
    margin: 0;
  }

  h2 {
    font-size: 20px;
  }
  h3 {
    font-size: 18px;
  }

  ul {
    padding-left: 1rem;
    list-style-type: disc;
    margin-bottom: 1rem;
    li {
      font-size: 13px;
      line-height: 1.9;
    }
    span {
      margin-right: 4px;
      font-weight: 700;
    }
  }

  table {
    border: 0;
    min-width: 50%;
    width: auto !important;
    th {
      border: 0;
    }
    tr {
      background: #f7f7f7;
      &:nth-child(even) {
        background: white;
      }

      td {
        padding: 7px 10px;
        text-align: right;
        font-size: 13px;
        line-height: 1.6;
        border: 0;
        &:nth-child(1) {
          text-align: left;
        }
      }
    }
  }
  .product-description-text {
    max-width: 70%;
  }
`;

const SpecificationsWrapper = styled('div')`
  display: flex;
  :nth-of-type(odd) {
    background: #f7f7f7;
  }

  .title {
    font-weight: 700;
    text-transform: uppercase;
    min-width: 20%;
    font-size: 12px;
    margin: 5px 0 1px 10px;
    letter-spacing: 1.1px;
  }

  .text {
    font-size: 12px;
    margin: 5px 0 1px 10px;
    letter-spacing: 1.1px;
  }
`;

const ProductTabs = ({
  product,
  remoteOpenIndex,
  setRemoteOpenIndex,
  brandsCategoryResult
}) => {
  const excludedCustomFields = ['files', 'Variants'];

  const filteredCustomFields = product?.customFields?.filter(
    customField => !excludedCustomFields.includes(customField.key)
  );

  const relatedProducts = product?.relatedProducts;
  const brand = product?.customFields?.filter(
    customField => customField?.key === 'Varumarke'
  );
  const files = product?.customFields?.filter(
    customField => customField.key === 'files'
  );

  const specifications = filteredCustomFields;

  //saved for possible future use
  const hasSpecifications = specifications?.length > 0;
  const hasRelatedProducts = product?.relatedProducts?.length > 0;
  const hasFiles = files?.length > 0;
  const hasBrand = !!brand;
  const hasDescription = !!product?.description;

  const brandInformationData = brandsCategoryResult?.find(
    subcat =>
      subcat?.name?.toLowerCase() === brand?.[0]?.stringValue?.toLowerCase()
  );

  const handleClick = index => {
    setRemoteOpenIndex(index);
  };

  return (
    <>
      <ButtonRow>
        <div className="inner">
          {hasDescription && (
            <Toggle
              className={remoteOpenIndex === 0 && 'active'}
              onClick={() => handleClick(0)}
            >
              {t('Product description')}
            </Toggle>
          )}
          {hasSpecifications && (
            <Toggle
              className={remoteOpenIndex === 1 && 'active'}
              onClick={() => handleClick(1)}
            >
              {t('Specifications')}
            </Toggle>
          )}
          {hasRelatedProducts && (
            <Toggle
              className={remoteOpenIndex === 2 && 'active'}
              onClick={() => handleClick(2)}
            >
              {t('Accessories')}
            </Toggle>
          )}
          {hasFiles && (
            <Toggle
              className={remoteOpenIndex === 3 && 'active'}
              onClick={() => handleClick(3)}
            >
              {t('Attached files')}
            </Toggle>
          )}
          {/* {hasBrand && (
            <Toggle
              className={remoteOpenIndex === 4 && 'active'}
              onClick={() => handleClick(4)}
            >
              {t('About ')}
              {brand?.[0]?.stringValue}
            </Toggle>
          )} */}
        </div>
      </ButtonRow>

      <TabContent>
        {hasDescription && (
          <TabInner
            style={{ display: remoteOpenIndex === 0 ? 'block' : 'none' }}
          >
            <div
              className="product-description-text"
              dangerouslySetInnerHTML={{
                __html: product?.description
              }}
            />
          </TabInner>
        )}
        {specifications && (
          <TabInner
            style={{ display: remoteOpenIndex === 1 ? 'block' : 'none' }}
          >
            {specifications?.map((specification, index) => {
              return (
                <SpecificationsWrapper key={index}>
                  <p className="title">{specification?.title}</p>
                  <p className="text"> {specification?.stringValue}</p>
                </SpecificationsWrapper>
              );
            })}
          </TabInner>
        )}
        {hasRelatedProducts && (
          <TabInner
            style={{ display: remoteOpenIndex === 2 ? 'block' : 'none' }}
          >
            {relatedProducts?.map((product, index) => {
              return (
                <LoadableProductListCard
                  key={product?.articleNumber}
                  product={product}
                  as="div"
                />
              );
            })}
          </TabInner>
        )}
        {hasFiles && (
          <TabInner
            style={{ display: remoteOpenIndex === 3 ? 'block' : 'none' }}
          >
            <DownloadableFiles files={files} />
          </TabInner>
        )}
        {/* {hasBrand && (
          <TabInner
            style={{ display: remoteOpenIndex === 4 ? 'block' : 'none' }}
          >
            {brandInformationData?.id && (
              <div>
                <DynamicCategoryRenderer
                  categoryId={brandInformationData?.id}
                />
              </div>
            )}
          </TabInner>
        )}  */}
      </TabContent>
    </>
  );
};

export default ProductTabs;
